import React from 'react';
import Flatpickr, { DateTimePickerProps } from 'react-flatpickr';
import { FormFieldProps } from '@nintendo/forms';
import moment from 'moment-timezone';
import { Field, FieldProps } from 'formik';

interface DateSearchFieldProps extends FormFieldProps {
    // Custom onChange function to call when date changes
    onChange?: (dateString: string) => void;
    // Timezone to convert the selected date into
    timezone?: string;
    // Flatpickr options
    flatpickrProps?: DateTimePickerProps;
    // Add clear icon for clearing out the input field
    clearable?: boolean;
    disabled?: boolean;
    placeholder?: string;
}

export const defaultDateFormat = 'YYYY-MM-DD hh:mm A z';

const DateSearchField = (props: DateSearchFieldProps) => {
    const { contentCssClass, placeholder } = props;

    const clearable = () => {
        if (props.clearable) {
            return (
                <span className="input-group-addon" data-clear>
                    <span className="far fa-trash-alt pointer" title="Remove date" />
                </span>
            );
        }
        return null;
    };

    const timezone = props.timezone || moment.tz.guess();
    const dateFormat =
        props.flatpickrProps &&
        props.flatpickrProps.options &&
        props.flatpickrProps.options.dateFormat
            ? props.flatpickrProps.options.dateFormat
            : defaultDateFormat;

    return (
        <div className={contentCssClass}>
            <Field name={props.name}>
                {({ field, form }: FieldProps<FormFieldProps>) => (
                    <Flatpickr
                        data-enable-time
                        {...props.flatpickrProps}
                        options={{
                            wrap: true,
                            allowInput: false,
                            dateFormat,
                            clickOpens: !props.disabled,
                            defaultDate: moment.tz(field.value, timezone).format(),
                            enableTime: true,
                            minuteIncrement: 10,
                            defaultHour: 12,
                            parseDate(dateString: string, format: string) {
                                const timezonedDate = moment.tz(dateString, format, timezone);

                                return new Date(
                                    timezonedDate.year(),
                                    timezonedDate.month(),
                                    timezonedDate.date(),
                                    timezonedDate.hour(),
                                    timezonedDate.minute(),
                                    timezonedDate.second(),
                                );
                            },
                            formatDate(date: Date, format: string) {
                                return moment
                                    .tz(
                                        [
                                            date.getFullYear(),
                                            date.getMonth(),
                                            date.getDate(),
                                            date.getHours(),
                                            date.getMinutes(),
                                            date.getSeconds(),
                                        ],
                                        timezone,
                                    )
                                    .format(format);
                            },
                            static: true,
                            ...(props.flatpickrProps ? props.flatpickrProps.options : {}),
                        }}
                        onChange={(selectedDates, dateString) => {
                            const utcDate = moment
                                .tz(dateString, dateFormat, timezone)
                                .toISOString();

                            if (props.onChange) {
                                props.onChange(utcDate);
                            }
                            form.setFieldValue(props.name, utcDate);
                        }}
                        onOpen={() => {
                            form.setFieldTouched(props.name, true);
                        }}
                        render={(pickrProps, ref) => (
                            <div data-testid="date-field-wrapper" className="input-group" ref={ref}>
                                <div className="input-group-prepend">
                                    <div className="input-group-text">
                                        <span className="input-group-addon" data-toggle>
                                            <span className="far fa-calendar-alt pointer" />
                                        </span>
                                    </div>
                                </div>

                                <input
                                    data-testid={props.testId}
                                    type={pickrProps.type}
                                    name={field.name}
                                    id={field.name}
                                    disabled={props.disabled ? true : undefined}
                                    className="form-control flatpickr-input active"
                                    placeholder={placeholder}
                                    data-input
                                />
                                {clearable()}
                            </div>
                        )}
                    />
                )}
            </Field>
        </div>
    );
};

export default DateSearchField;
